import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import api from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import Table from '../../components/common/Table';
import Pagination from '../../components/common/Pagination';
import { LuPlus, LuPencil, LuCalendarCheck, LuX, LuMapPin, LuBan, LuEye } from 'react-icons/lu';
import { useAuth } from '../../context/AuthContext';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { debounce, selectStyles } from '../../utils/selectConfig';

const EMPTY = { company: '', driver: '', employees: [], from: '', to: '', tripDate: '', departureTime: '', passengerCount: 0, notes: '', status: 'scheduled' };

const STATUS_OPTS = ['scheduled', 'on_the_way', 'at_pickup', 'in_progress', 'completed', 'cancelled'];

const STATUS_LABELS = {
  scheduled:   'Scheduled',
  on_the_way:  'On the Way',
  at_pickup:   'At Pickup',
  in_progress: 'In Progress',
  completed:   'Completed',
  cancelled:   'Cancelled',
};

const STATUS_COLORS = {
  scheduled:   'bg-hajj-teal/10 text-teal-700',
  on_the_way:  'bg-blue-50 text-blue-600',
  at_pickup:   'bg-purple-50 text-purple-600',
  in_progress: 'bg-hajj-gold/15 text-amber-700',
  completed:   'bg-green-50 text-green-700',
  cancelled:   'bg-red-50 text-red-600',
};

function RouteSnapshot({ from, to, apiKey }) {
  const [imgError,    setImgError]    = useState(false);
  const [polyline,    setPolyline]    = useState(null);
  const [routeExists, setRouteExists] = useState(null); // null=loading, true/false after fetch

  useEffect(() => {
    setPolyline(null);
    setImgError(false);
    setRouteExists(null);
    if (!from?.id || !to?.id) return;
    api.get(`/routes?from=${from.id}&to=${to.id}&limit=1`)
      .then(res => {
        const route = res.data.data?.[0];
        setRouteExists(!!route);
        if (route?.encodedPolyline) setPolyline(route.encodedPolyline);
      })
      .catch(() => setRouteExists(false));
  }, [from?.id, to?.id]);

  const noRouteNotice = routeExists === false && (
    <div className="col-span-1 sm:col-span-2 flex items-start gap-2.5 px-3.5 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-700">
      <LuMapPin size={13} className="mt-0.5 shrink-0 text-amber-500" />
      <span>
        No saved route found for <strong>{from.label} → {to.label}</strong>.
        Go to <strong>Routes</strong> and add this route to get accurate path and distance data.
      </span>
    </div>
  );

  if (!apiKey || imgError || !from?.lat || !to?.lat) {
    return (
      <>
        <div className="col-span-1 sm:col-span-2 h-14 rounded-xl bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center gap-2 text-gray-400 text-xs">
          <LuMapPin size={13} />
          <span className="font-medium text-gray-500">{from.label}</span>
          <span className="text-gray-300">→</span>
          <span className="font-medium text-gray-500">{to.label}</span>
        </div>
        {noRouteNotice}
      </>
    );
  }

  const pathParam = polyline
    ? `enc:${encodeURIComponent(polyline)}`
    : `color:0x2d5a4aCC%7Cweight:4%7C${from.lat},${from.lng}%7C${to.lat},${to.lng}`;

  const url = [
    'https://maps.googleapis.com/maps/api/staticmap',
    '?size=640x200&maptype=roadmap',
    `&markers=color:green%7Clabel:A%7C${from.lat},${from.lng}`,
    `&markers=color:red%7Clabel:B%7C${to.lat},${to.lng}`,
    `&path=${pathParam}`,
    `&key=${apiKey}`,
  ].join('');

  return (
    <>
      <div className="col-span-1 sm:col-span-2">
        <img
          src={url}
          alt={`${from.label} → ${to.label}`}
          onError={() => setImgError(true)}
          className="w-full rounded-xl border border-gray-100 object-cover"
          style={{ height: '200px' }}
        />
      </div>
      {noRouteNotice}
    </>
  );
}

export default function Trips() {
  const { isAdmin } = useAuth();
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);   // still needed for the filter dropdown
  const [loading, setLoading] = useState(true);

  const [datePreset, setDatePreset] = useState('today');
  const [filterDateFrom, setFilterDateFrom] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [filterDateTo, setFilterDateTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [filterDriver, setFilterDriver] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterAgent, setFilterAgent] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCompanyOpt, setFilterCompanyOpt] = useState(null);
  const [filterDriverOpt, setFilterDriverOpt] = useState(null);
  const [filterAgentOpt, setFilterAgentOpt] = useState(null);

  const [modal, setModal] = useState({ open: false, mode: 'add', data: null });
  const [viewTrip, setViewTrip] = useState(null);
  const [viewTripOpts, setViewTripOpts] = useState({ from: null, to: null });
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const PAGE_SIZE = 10;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [mapsApiKey, setMapsApiKey] = useState('');

  // react-select value objects (needed to display labels alongside IDs)
  const [companyOpt, setCompanyOpt] = useState(null);
  const [driverOpt, setDriverOpt] = useState(null);
  const [agentOpt, setAgentOpt] = useState(null);
  const [fromOpt, setFromOpt] = useState(null);
  const [toOpt, setToOpt] = useState(null);

  // Ref so loadAgents closure always reads the current company without needing it as a dep
  const companyRef = useRef('');
  useEffect(() => { companyRef.current = form.company; }, [form.company]);

  const loadStatic = useCallback(async () => {
    const [dRes, sRes] = await Promise.all([
      api.get('/drivers?limit=10'),
      api.get('/settings/google_maps_api_key').catch(() => null),
    ]);
    setDrivers(dRes.data.data || []);
    if (sRes) setMapsApiKey(sRes.data.data?.value || '');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (filterStatus)   params.set('status',   filterStatus);
      if (filterCompany)  params.set('company',  filterCompany);
      if (filterDriver)   params.set('driver',   filterDriver);
      if (filterAgent)    params.set('employee', filterAgent);
      if (filterDateFrom) params.set('fromDate', filterDateFrom);
      if (filterDateTo)   params.set('toDate',   filterDateTo);
      const { data } = await api.get(`/trips?${params}`);
      setTrips(data.data || []);
      setPagination(data.pagination || null);
    } catch { toast.error('Failed to load trips'); }
    finally { setLoading(false); }
  }, [page, filterStatus, filterCompany, filterDriver, filterAgent, filterDateFrom, filterDateTo]);

  useEffect(() => { loadStatic(); }, [loadStatic]);
  useEffect(() => { load(); }, [load]);

  // ── AsyncSelect load functions (debounced, 10 records each) ─────────────────

  const loadCompanies = useMemo(() => debounce(async (q) => {
    const { data } = await api.get(`/companies?search=${encodeURIComponent(q)}&limit=10`);
    return (data.data || []).map(c => ({ value: c._id, label: c.name }));
  }, 300), []);

  const loadDrivers_ = useMemo(() => debounce(async (q) => {
    const { data } = await api.get(`/drivers?search=${encodeURIComponent(q)}&limit=10`);
    return (data.data || []).map(d => ({
      value: d._id,
      label: d.vehicle?.number ? `${d.name} · ${d.vehicle.number}` : d.name,
    }));
  }, 300), []);

  const locFetcher = async (q) => {
    const { data } = await api.get(`/locations?status=active&search=${encodeURIComponent(q)}&limit=10`);
    return (data.data || []).map(l => ({ value: l.name, label: l.name, lat: l.lat, lng: l.lng, id: l._id }));
  };
  // Separate instances so simultaneous defaultOptions calls don't cancel each other
  const loadLocationsFrom = useMemo(() => debounce(locFetcher, 300), []); // separate instances intentional
  const loadLocationsTo   = useMemo(() => debounce(locFetcher, 300), []);

  const loadAgents = useMemo(() => debounce(async (q) => {
    const params = new URLSearchParams({ search: q, limit: '10' });
    if (companyRef.current) params.set('company', companyRef.current);
    const { data } = await api.get(`/employees?${params}`);
    return (data.data || []).map(e => ({ value: e._id, label: `${e.name} · ${e.phone}` }));
  }, 300), []);

  const loadAgentsFilter = useMemo(() => debounce(async (q) => {
    const { data } = await api.get(`/employees?search=${encodeURIComponent(q)}&limit=10`);
    return (data.data || []).map(e => ({ value: e._id, label: `${e.name} · ${e.phone}` }));
  }, 300), []);

  // ── Form helpers ─────────────────────────────────────────────────────────────

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const resetOpts = () => { setCompanyOpt(null); setDriverOpt(null); setAgentOpt(null); setFromOpt(null); setToOpt(null); };

  const openAdd = () => { setForm(EMPTY); resetOpts(); setModal({ open: true, mode: 'add', data: null }); };

  const openEdit = async r => {
    setForm({
      company:   r.company?._id   || r.company   || '',
      driver:    r.driver?._id    || r.driver    || '',
      employees: r.employees?.map(e => e._id || e) || [],
      from: r.from, to: r.to,
      tripDate: r.tripDate ? r.tripDate.split('T')[0] : '',
      departureTime: r.departureTime,
      passengerCount: r.passengerCount || 0,
      notes: r.notes || '',
      status: r.status,
    });
    setCompanyOpt(r.company?._id ? { value: r.company._id, label: r.company.name } : null);
    setDriverOpt(r.driver?._id
      ? { value: r.driver._id, label: r.driver.vehicle?.number ? `${r.driver.name} · ${r.driver.vehicle.number}` : r.driver.name }
      : null);
    const agent = r.employees?.[0];
    setAgentOpt(agent?._id ? { value: agent._id, label: `${agent.name} · ${agent.phone}` } : null);

    // Fetch lat/lng for existing from/to so the static map can render
    const [fromRes, toRes] = await Promise.all([
      r.from ? api.get(`/locations?search=${encodeURIComponent(r.from)}&limit=1`).catch(() => null) : null,
      r.to   ? api.get(`/locations?search=${encodeURIComponent(r.to)}&limit=1`).catch(() => null)   : null,
    ]);
    const fromLoc = fromRes?.data?.data?.[0];
    const toLoc   = toRes?.data?.data?.[0];
    setFromOpt(r.from ? { value: r.from, label: r.from, lat: fromLoc?.lat, lng: fromLoc?.lng, id: fromLoc?._id } : null);
    setToOpt(r.to     ? { value: r.to,   label: r.to,   lat: toLoc?.lat,   lng: toLoc?.lng,   id: toLoc?._id   } : null);

    setModal({ open: true, mode: 'edit', data: r });
  };

  const openView = async r => {
    setViewTripOpts({ from: null, to: null });
    setViewTrip(r);
    const [fromRes, toRes] = await Promise.all([
      r.from ? api.get(`/locations?search=${encodeURIComponent(r.from)}&limit=1`).catch(() => null) : null,
      r.to   ? api.get(`/locations?search=${encodeURIComponent(r.to)}&limit=1`).catch(() => null)   : null,
    ]);
    const fromLoc = fromRes?.data?.data?.[0];
    const toLoc   = toRes?.data?.data?.[0];
    setViewTripOpts({
      from: r.from ? { value: r.from, label: r.from, lat: fromLoc?.lat, lng: fromLoc?.lng, id: fromLoc?._id } : null,
      to:   r.to   ? { value: r.to,   label: r.to,   lat: toLoc?.lat,   lng: toLoc?.lng,   id: toLoc?._id   } : null,
    });
  };

  const close = () => { setModal({ open: false, mode: 'add', data: null }); resetOpts(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isAdmin && !form.driver) { toast.error('Driver is required'); return; }
    if (isAdmin && !form.employees.length) { toast.error('Hajj Agent is required'); return; }
    if (form.from && form.to && form.from === form.to) {
      toast.error('From and To locations cannot be the same'); return;
    }
    setSaving(true);
    try {
      if (modal.mode === 'add') await api.post('/trips', form);
      else await api.put(`/trips/${modal.data._id}`, form);
      toast.success(modal.mode === 'add' ? 'Trip scheduled!' : 'Updated!');
      close(); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error saving'); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/trips/${id}/status`, { status }); toast.success('Status updated'); load(); }
    catch { toast.error('Failed'); }
  };

  const cancelTrip = async () => {
    if (!cancelConfirm) return;
    try { await api.patch(`/trips/${cancelConfirm._id}/cancel`); toast.success('Trip cancelled'); setCancelConfirm(null); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed to cancel'); }
  };

  const DATE_PRESETS = [
    { key: 'today',      label: 'Today' },
    { key: 'this_week',  label: 'This Week' },
    { key: 'this_month', label: 'This Month' },
    { key: 'this_year',  label: 'This Year' },
    { key: 'custom',     label: 'Custom' },
  ];

  const applyPreset = (key) => {
    const now = new Date();
    const fmt = d => format(d, 'yyyy-MM-dd');
    setDatePreset(key);
    if (key === 'today')      { setFilterDateFrom(fmt(now));                              setFilterDateTo(fmt(now)); }
    if (key === 'this_week')  { setFilterDateFrom(fmt(startOfWeek(now, { weekStartsOn: 1 }))); setFilterDateTo(fmt(endOfWeek(now, { weekStartsOn: 1 }))); }
    if (key === 'this_month') { setFilterDateFrom(fmt(startOfMonth(now)));                setFilterDateTo(fmt(endOfMonth(now))); }
    if (key === 'this_year')  { setFilterDateFrom(fmt(startOfYear(now)));                 setFilterDateTo(fmt(endOfYear(now))); }
    if (key === 'custom')     { setFilterDateFrom(''); setFilterDateTo(''); }
  };

  const today = format(new Date(), 'yyyy-MM-dd');
  const hasFilter = datePreset !== 'today' || filterDriver || filterCompany || filterAgent || filterStatus;
  const clearAll = () => {
    applyPreset('today');
    setFilterDriver(''); setFilterCompany(''); setFilterAgent(''); setFilterStatus('');
    setFilterCompanyOpt(null); setFilterDriverOpt(null); setFilterAgentOpt(null);
  };

  // reset page when filters change
  const filterKey = `${filterStatus}|${filterCompany}|${filterDriver}|${filterAgent}|${filterDateFrom}|${filterDateTo}`;
  const prevFilterKey = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKey.current !== filterKey) { prevFilterKey.current = filterKey; setPage(1); }
  });

  const columns = [
    { key: 'tripNumber', label: '#', width: 100, render: r => (
      <span className="font-mono font-semibold text-hajj-teal text-xs whitespace-nowrap">
        {r.tripNumber ? `TRP-${String(r.tripNumber).padStart(3, '0')}` : '—'}
      </span>
    )},
    { key: 'route', label: 'Trip', width: 200, render: r => (
      <div className="flex flex-col gap-0.5 font-semibold text-hajj-dark text-xs max-w-[180px]" title={`${r.from} → ${r.to}`}>
        <span className="truncate">{r.from}</span>
        <span className="flex items-center gap-1 text-gray-400 font-normal">
          <span className="text-hajj-gold">→</span>
          <span className="truncate text-hajj-dark font-semibold">{r.to}</span>
        </span>
      </div>
    )},
    { key: 'tripDate', label: 'Date', render: r => (
      <span className="font-medium whitespace-nowrap">{r.tripDate ? format(new Date(r.tripDate), 'dd MMM yyyy') : '—'}</span>
    )},
    { key: 'departureTime', label: 'Schedule Time', render: r => (
      <span className="text-sm font-medium text-gray-700 whitespace-nowrap">{r.departureTime || '—'}</span>
    )},
    { key: 'startTime', label: 'Start Time', render: r => r.startTime
      ? <span className="text-sm font-medium text-blue-600 whitespace-nowrap">{format(new Date(r.startTime), 'HH:mm')}</span>
      : <span className="text-gray-300 text-xs">—</span>
    },
    { key: 'endTime', label: 'End Time', render: r => r.endTime
      ? <span className="text-sm font-medium text-green-600 whitespace-nowrap">{format(new Date(r.endTime), 'HH:mm')}</span>
      : <span className="text-gray-300 text-xs">—</span>
    },
    { key: 'duration', label: 'Duration', render: r => {
      if (!r.startTime || !r.endTime) return <span className="text-gray-300 text-xs">—</span>;
      const m = Math.floor((new Date(r.endTime) - new Date(r.startTime)) / 60000);
      const label = m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;
      return <span className="text-sm font-semibold text-amber-600 whitespace-nowrap">{label}</span>;
    }},
    { key: 'company', label: 'Hajj Company', render: r => r.company
      ? <span className="font-medium text-hajj-dark">{r.company.name}</span>
      : <span className="text-gray-300 text-xs">—</span>
    },
    { key: 'agent', label: 'Hajj Agent', render: r => {
      const agent = r.employees?.[0];
      return agent
        ? <div><div className="font-medium text-hajj-dark">{agent.name}</div><div className="text-xs text-gray-400">{agent.phone}</div></div>
        : <span className="text-gray-300 text-xs">—</span>;
    }},
    { key: 'driver', label: 'Driver', render: r => r.driver ? (
      <div>
        <div className="font-medium text-hajj-dark whitespace-nowrap">{r.driver.name}</div>
        <div className="text-xs text-gray-400">{r.driver.vehicle?.number || ''}</div>
      </div>
    ) : <span className="text-gray-300 text-xs">Unassigned</span>},
    { key: 'status', label: 'Status', render: r => (
      <select value={r.status} onChange={e => updateStatus(r._id, e.target.value)}
        className={`badge cursor-pointer border-0 outline-none bg-transparent ${STATUS_COLORS[r.status]}`}
        style={{ appearance: 'auto' }}>
        {STATUS_OPTS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
      </select>
    )},
    { key: 'actions', label: 'Action', width: 110, render: r => (
      <div className="flex gap-1.5">
        <button onClick={() => openView(r)} title="View details" className="w-8 h-8 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-gray-200 transition-colors"><LuEye size={14} /></button>
        {!['completed', 'cancelled'].includes(r.status) && (
          <button onClick={() => openEdit(r)} title="Edit trip" className="w-8 h-8 rounded-lg bg-hajj-teal/10 text-teal-700 flex items-center justify-center hover:bg-hajj-teal/20 transition-colors"><LuPencil size={14} /></button>
        )}
        {isAdmin && !['completed', 'cancelled'].includes(r.status) && (
          <button onClick={() => setCancelConfirm(r)} title="Cancel trip" className="w-8 h-8 rounded-lg bg-red-50 text-red-400 flex items-center justify-center hover:bg-red-100 hover:text-red-600 transition-colors"><LuBan size={14} /></button>
        )}
      </div>
    )},
  ];

  return (
    <div className="flex flex-col gap-5">

      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 text-hajj-dark">
          <LuCalendarCheck className="text-xl sm:text-2xl" />
          <h1 className="text-xl sm:text-2xl font-extrabold">Trips</h1>
        </div>
        {isAdmin && <button className="btn-primary text-sm" onClick={openAdd}><LuPlus size={15} /> Schedule Trip</button>}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2.5">
        {/* Row 1: Date + inline custom inputs, then entity filters */}
        <div className="flex flex-wrap gap-2.5 items-center">
          <div className="w-[160px] shrink-0">
            <Select
              options={DATE_PRESETS.map(p => ({ value: p.key, label: p.label }))}
              placeholder="All Dates"
              value={{ value: datePreset, label: DATE_PRESETS.find(p => p.key === datePreset)?.label }}
              onChange={opt => applyPreset(opt?.value || 'today')}
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
          {datePreset === 'custom' && (
            <>
              <input type="date" value={filterDateFrom} max={filterDateTo || undefined} onChange={e => { setFilterDateFrom(e.target.value); if (filterDateTo && e.target.value > filterDateTo) setFilterDateTo(e.target.value); }} className="input-field text-sm w-[150px] shrink-0" />
              <span className="text-gray-400 text-xs shrink-0">to</span>
              <input type="date" value={filterDateTo} min={filterDateFrom || undefined} onChange={e => setFilterDateTo(e.target.value)} className="input-field text-sm w-[150px] shrink-0" />
            </>
          )}
          {isAdmin && (
            <div className="flex-1 min-w-[170px]">
              <AsyncSelect
                loadOptions={loadCompanies}
                defaultOptions
                cacheOptions
                isClearable
                placeholder="All Companies"
                value={filterCompanyOpt}
                onChange={opt => { setFilterCompanyOpt(opt); setFilterCompany(opt?.value || ''); }}
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>
          )}
          <div className="flex-1 min-w-[170px]">
            <AsyncSelect
              loadOptions={loadDrivers_}
              defaultOptions
              cacheOptions
              isClearable
              placeholder="All Drivers"
              value={filterDriverOpt}
              onChange={opt => { setFilterDriverOpt(opt); setFilterDriver(opt?.value || ''); }}
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
          <div className="flex-1 min-w-[170px]">
            <AsyncSelect
              loadOptions={loadAgentsFilter}
              defaultOptions
              cacheOptions
              isClearable
              placeholder="All Agents"
              value={filterAgentOpt}
              onChange={opt => { setFilterAgentOpt(opt); setFilterAgent(opt?.value || ''); }}
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
        </div>

        {/* Row 2: Status + Clear */}
        <div className="flex flex-wrap gap-2.5 items-center">
          <div className="w-[160px] shrink-0">
            <Select
              options={STATUS_OPTS.map(s => ({ value: s, label: STATUS_LABELS[s] }))}
              isClearable
              placeholder="All Statuses"
              value={filterStatus ? { value: filterStatus, label: STATUS_LABELS[filterStatus] } : null}
              onChange={opt => setFilterStatus(opt?.value || '')}
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
          {hasFilter && (
            <button onClick={clearAll} className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg border border-gray-200 transition-colors">
              <LuX size={11} /> Clear
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <Table columns={columns} data={trips} loading={loading} emptyMessage="No trips found" />
        <Pagination page={page} total={pagination?.total || 0} pageSize={PAGE_SIZE} onPage={setPage} />
      </div>

      {/* Cancel Trip Confirmation Modal */}
      <Modal isOpen={!!cancelConfirm} onClose={() => setCancelConfirm(null)} title="Cancel Trip" size="sm">
        {cancelConfirm && (
          <div className="flex flex-col gap-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <LuBan size={18} className="text-red-500" />
              </div>
              <div>
                <p className="text-sm text-gray-700">Are you sure you want to cancel this trip?</p>
                <p className="mt-1 text-sm font-semibold text-hajj-dark">
                  {cancelConfirm.from} → {cancelConfirm.to}
                </p>
                {cancelConfirm.tripDate && (
                  <p className="text-xs text-gray-400 mt-0.5">{format(new Date(cancelConfirm.tripDate), 'dd MMM yyyy')}{cancelConfirm.departureTime ? ` · ${cancelConfirm.departureTime}` : ''}</p>
                )}
                <p className="mt-2 text-xs text-red-500">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button className="btn-secondary" onClick={() => setCancelConfirm(null)}>Keep Trip</button>
              <button className="btn-danger" onClick={cancelTrip}>Yes, Cancel Trip</button>
            </div>
          </div>
        )}
      </Modal>

      {/* View Trip Details Modal */}
      <Modal isOpen={!!viewTrip} onClose={() => { setViewTrip(null); setViewTripOpts({ from: null, to: null }); }} title="Trip Details" size="md">
        {viewTrip && (() => {
          const t = viewTrip;
          const tripNum = t.tripNumber ? `TRP-${String(t.tripNumber).padStart(3, '0')}` : '—';
          const startFmt = t.startTime ? format(new Date(t.startTime), 'HH:mm') : '—';
          const endFmt   = t.endTime   ? format(new Date(t.endTime),   'HH:mm') : '—';
          const duration = t.startTime && t.endTime ? (() => {
            const m = Math.floor((new Date(t.endTime) - new Date(t.startTime)) / 60000);
            return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;
          })() : '—';
          const rows = [
            { label: 'Trip #',         value: tripNum },
            { label: 'Route',          value: `${t.from} → ${t.to}` },
            { label: 'Date',           value: t.tripDate ? format(new Date(t.tripDate), 'dd MMM yyyy') : '—' },
            { label: 'Schedule Time',  value: t.departureTime || '—' },
            { label: 'Start Time',     value: startFmt },
            { label: 'End Time',       value: endFmt },
            { label: 'Duration',       value: duration },
            { label: 'Status',         value: <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[t.status]}`}>{STATUS_LABELS[t.status]}</span> },
            ...(isAdmin ? [{ label: 'Company',   value: t.company?.name || '—' }] : []),
            { label: 'Driver',         value: t.driver ? `${t.driver.name}${t.driver.phone ? ` · ${t.driver.phone}` : ''}` : '—' },
            { label: 'Hajj Agent',     value: t.employees?.length ? t.employees.map(e => e.name).join(', ') : '—' },
            { label: 'Notes',          value: t.notes || '—' },
          ];
          return (
            <div className="flex flex-col gap-3">
              {viewTripOpts.from?.value && viewTripOpts.to?.value && (
                <div className="grid grid-cols-1">
                  <RouteSnapshot from={viewTripOpts.from} to={viewTripOpts.to} apiKey={mapsApiKey} />
                </div>
              )}
              {rows.map(({ label, value }) => (
                <div key={label} className="flex items-start gap-3 text-sm">
                  <span className="w-28 shrink-0 text-xs font-semibold text-gray-400 uppercase tracking-wide pt-0.5">{label}</span>
                  <span className="text-gray-800">{value}</span>
                </div>
              ))}
              <div className="flex justify-end pt-2">
                <button className="btn-secondary" onClick={() => { setViewTrip(null); setViewTripOpts({ from: null, to: null }); }}>Close</button>
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal isOpen={modal.open} onClose={close} title={modal.mode === 'add' ? 'Schedule Trip' : 'Edit Trip'} size="lg">
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">

          {/* Company / Agent / Driver — admin only */}
          {isAdmin && (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Company *</label>
                  <AsyncSelect
                    loadOptions={loadCompanies}
                    defaultOptions
                    cacheOptions
                    isClearable
                    placeholder="Search company..."
                    value={companyOpt}
                    onChange={opt => {
                      setCompanyOpt(opt);
                      setAgentOpt(null);
                      setForm(f => ({ ...f, company: opt?.value || '', employees: [] }));
                    }}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Hajj Agent *</label>
                  <AsyncSelect
                    key={form.company}
                    loadOptions={loadAgents}
                    defaultOptions={!!form.company}
                    cacheOptions
                    isClearable
                    isDisabled={!form.company}
                    placeholder={form.company ? 'Search agent...' : 'Select company first'}
                    value={agentOpt}
                    onChange={opt => {
                      setAgentOpt(opt);
                      setForm(f => ({ ...f, employees: opt ? [opt.value] : [] }));
                    }}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Driver *</label>
                  <AsyncSelect
                    loadOptions={loadDrivers_}
                    defaultOptions
                    cacheOptions
                    isClearable
                    placeholder="Search driver..."
                    value={driverOpt}
                    onChange={opt => {
                      setDriverOpt(opt);
                      setForm(f => ({ ...f, driver: opt?.value || '' }));
                    }}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>
            </div>
          )}

          {/* From / To / Date / Time / Passengers / Status / Notes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="col-span-1 sm:col-span-2">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Trip Details</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">From *</label>
              <AsyncSelect
                loadOptions={loadLocationsFrom}
                defaultOptions
                cacheOptions
                isClearable
                placeholder="Search location..."
                value={fromOpt}
                filterOption={opt => opt.value !== form.to}
                onChange={opt => { setFromOpt(opt); setForm(f => ({ ...f, from: opt?.value || '' })); }}
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">To *</label>
              <AsyncSelect
                loadOptions={loadLocationsTo}
                defaultOptions
                cacheOptions
                isClearable
                placeholder="Search location..."
                value={toOpt}
                filterOption={opt => opt.value !== form.from}
                onChange={opt => { setToOpt(opt); setForm(f => ({ ...f, to: opt?.value || '' })); }}
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>

            {/* Route map — static snapshot or text fallback */}
            {fromOpt?.value && toOpt?.value && (
              <RouteSnapshot from={fromOpt} to={toOpt} apiKey={mapsApiKey} />
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Trip Date *</label>
              <input type="date" value={form.tripDate} onChange={set('tripDate')} required className="input-field" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Trip Time *</label>
              <input type="time" value={form.departureTime} onChange={set('departureTime')} required className="input-field" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Passengers</label>
              <input type="number" value={form.passengerCount} onChange={set('passengerCount')} min={0} className="input-field" />
            </div>
            {modal.mode === 'edit' && (
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Status</label>
                <select value={form.status} onChange={set('status')} className="input-field">
                  {STATUS_OPTS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
              </div>
            )}
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Notes</label>
              <textarea value={form.notes} onChange={set('notes')} rows={2} className="input-field resize-none" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" className="btn-secondary" onClick={close}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              {modal.mode === 'add' ? 'Schedule Trip' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
